import { supabase } from "@/integrations/supabase/client";
import { handleSupabaseFunctionError } from "@/utils/supabaseFunctionsError";
import type {
  ExportTargetareCsvResult,
  GetTargetareFirmaResponse,
  GetTargetareStatsResponse,
  GetTargetareUniversCompletResponse,
  GetTargetareUniversParams,
  GetTargetareUniversResponse,
  RunTargetareClasificareRequest,
  RunTargetareClasificareResponse,
  RunTargetareRiscResponse,
} from "@/api/types/targetare";
import type { TargetareFiltre } from "@/types/targetare";
import { filtreTargetareToQuery } from "@/utils/targetareFiltre";

function assertSuccess(data: unknown, endpoint: string): void {
  if (
    !data ||
    typeof data !== "object" ||
    (data as { success?: unknown }).success !== true
  ) {
    throw new Error(`Răspuns invalid de la ${endpoint}`);
  }
}

export async function getTargetareStats(): Promise<GetTargetareStatsResponse> {
  const { data, error } = await supabase.functions.invoke(
    "api/targetare/stats",
    { method: "GET" }
  );
  await handleSupabaseFunctionError(error);
  assertSuccess(data, "targetare/stats");
  return data;
}

export async function getTargetareUnivers(
  params: GetTargetareUniversParams
): Promise<GetTargetareUniversResponse> {
  const query = filtreTargetareToQuery(params.filtre);
  query.set("pagina", String(params.pagina));
  query.set("per_pagina", String(params.perPagina));
  const { data, error } = await supabase.functions.invoke(
    `api/targetare/univers?${query.toString()}`,
    { method: "GET" }
  );
  await handleSupabaseFunctionError(error);
  assertSuccess(data, "targetare/univers");
  return data;
}

export async function getTargetareUniversComplet(
  filtre: TargetareFiltre
): Promise<GetTargetareUniversCompletResponse> {
  const query = filtreTargetareToQuery(filtre);
  const { data, error } = await supabase.functions.invoke(
    `api/targetare/univers/complet?${query.toString()}`,
    { method: "GET" }
  );
  await handleSupabaseFunctionError(error);
  assertSuccess(data, "targetare/univers/complet");
  return data;
}

export async function getTargetareFirma(
  cui: string
): Promise<GetTargetareFirmaResponse> {
  const { data, error } = await supabase.functions.invoke(
    `api/targetare/firma/${encodeURIComponent(cui)}`,
    { method: "GET" }
  );
  await handleSupabaseFunctionError(error);
  assertSuccess(data, "targetare/firma");
  return data;
}

export async function runTargetareClasificare(
  request: RunTargetareClasificareRequest
): Promise<RunTargetareClasificareResponse> {
  const { data, error } = await supabase.functions.invoke(
    "api/targetare/clasifica",
    { method: "POST", body: request }
  );
  await handleSupabaseFunctionError(error);
  assertSuccess(data, "targetare/clasifica");
  return data;
}

export async function runTargetareRisc(): Promise<RunTargetareRiscResponse> {
  const { data, error } = await supabase.functions.invoke(
    "api/targetare/risc",
    {
      method: "POST",
      body: {},
    }
  );
  await handleSupabaseFunctionError(error);
  assertSuccess(data, "targetare/risc");
  return data;
}

export async function exportTargetareCsv(
  filtre: TargetareFiltre
): Promise<ExportTargetareCsvResult> {
  const query = filtreTargetareToQuery(filtre);
  const { data, error } = await supabase.functions.invoke(
    `api/targetare/export?${query.toString()}`,
    { method: "GET" }
  );
  await handleSupabaseFunctionError(error);
  if (typeof data !== "string") {
    throw new Error("Răspuns invalid de la targetare/export");
  }
  const zi = new Date().toISOString().slice(0, 10);
  return { continut: data, numeFisier: `targetare-nis2-${zi}.csv` };
}
